import React, { useState, useRef, useCallback } from 'react';
import { BsBugFill } from 'react-icons/bs';
import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';

const PROXY_BASE = process.env.REACT_APP_GPT_API?.replace('/api/chat', '') || 'http://localhost:8020';

export default function FeedbackWidget() {
  const { user } = useAuth() || {};
  const ctx = useAppContext() || {};

  const [open, setOpen] = useState(false);       // modal visible
  const [mode, setMode] = useState(null);         // 'bug' | 'feature' | null (picker)
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null); // data URL
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);     // { ok, identifier, url } or { ok: false, error }
  const screenshotRef = useRef(null);

  // Capture screenshot via html2canvas
  const captureScreenshot = useCallback(async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        backgroundColor: '#060d18',
        scale: 0.75,
        logging: false,
        useCORS: true,
        ignoreElements: (el) => el.classList?.contains('feedback-widget-root'),
      });
      const dataUrl = canvas.toDataURL('image/png', 0.7);
      setScreenshot(dataUrl);
    } catch (e) {
      console.warn('Screenshot capture failed:', e);
    }
  }, []);

  const handleOpen = async () => {
    setOpen(true);
    setMode(null);
    setTitle('');
    setDescription('');
    setScreenshot(null);
    setResult(null);
  };

  const handleSelectMode = async (m) => {
    setMode(m);
    if (m === 'bug') {
      captureScreenshot();
    }
  };

  const handleClose = () => {
    setOpen(false);
    setMode(null);
    setResult(null);
  };

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      const token = user ? await user.getIdToken() : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const body = {
        type: mode,
        title: title.trim(),
        description: description.trim(),
        userQuery: ctx.input || '',
        userEmail: user?.email || '',
        pageUrl: window.location.href,
        project: 'version2-ai-discovery-engine',
        screenshot: mode === 'bug' ? screenshot : null,
      };

      const resp = await fetch(`${PROXY_BASE}/api/feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (data.ok) {
        setResult({ ok: true, identifier: data.issue?.identifier, url: data.issue?.url });
      } else {
        setResult({ ok: false, error: data.error || 'Unknown error' });
      }
    } catch (e) {
      setResult({ ok: false, error: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="feedback-widget-root">
      {/* Floating button */}
      <button
        onClick={handleOpen}
        title="Report a bug or request a feature"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 10050,
          width: 48, height: 48, borderRadius: '50%',
          background: 'linear-gradient(135deg, #f43f5e, #e11d48)',
          border: 'none', cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(244,63,94,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(244,63,94,0.6)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(244,63,94,0.4)'; }}
      >
        <BsBugFill size={22} color="#fff" />
      </button>

      {/* Modal overlay */}
      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 10060,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <div style={{
            background: '#0f1923', borderRadius: 12, width: '100%', maxWidth: 560,
            border: '1px solid #1e293b', boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid #1e293b',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 16 }}>
                {!mode ? 'Send Feedback' : mode === 'bug' ? 'Report a Bug' : 'Request a Feature'}
              </span>
              <button onClick={handleClose} style={{
                background: 'none', border: 'none', color: '#64748b', fontSize: 18, cursor: 'pointer', padding: '0 4px',
              }}>x</button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px' }}>
              {/* Success / Error state */}
              {result ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  {result.ok ? (
                    <>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>{mode === 'bug' ? '\uD83D\uDC1B' : '\u2728'}</div>
                      <div style={{ color: '#4ade80', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
                        {mode === 'bug' ? 'Bug reported!' : 'Feature requested!'}
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>
                        Thanks for your feedback. Our team will review this shortly.
                      </div>
                      <div style={{ marginTop: 20 }}>
                        <button className="btn btn-sm btn-outline-light" onClick={handleClose}>Close</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ color: '#f87171', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Submission failed</div>
                      <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>{result.error}</div>
                      <button className="btn btn-sm btn-outline-light" onClick={() => setResult(null)}>Try Again</button>
                    </>
                  )}
                </div>
              ) : !mode ? (
                /* ── Type picker ─── */
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <button
                    onClick={() => handleSelectMode('bug')}
                    style={{
                      background: '#0a1120', border: '1px solid #1e293b', borderRadius: 10,
                      padding: '28px 16px', cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#f87171'; e.currentTarget.style.background = 'rgba(248,113,113,0.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e293b'; e.currentTarget.style.background = '#0a1120'; }}
                  >
                    <span style={{ fontSize: 32 }}>{'\uD83D\uDC1B'}</span>
                    <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15 }}>Report a Bug</span>
                    <span style={{ color: '#64748b', fontSize: 12 }}>Something isn't working right</span>
                  </button>
                  <button
                    onClick={() => handleSelectMode('feature')}
                    style={{
                      background: '#0a1120', border: '1px solid #1e293b', borderRadius: 10,
                      padding: '28px 16px', cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.background = 'rgba(167,139,250,0.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e293b'; e.currentTarget.style.background = '#0a1120'; }}
                  >
                    <span style={{ fontSize: 32 }}>{'\u2728'}</span>
                    <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15 }}>Request a Feature</span>
                    <span style={{ color: '#64748b', fontSize: 12 }}>Suggest an improvement</span>
                  </button>
                </div>
              ) : (
                /* ── Form ─── */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* Back button */}
                  <button
                    onClick={() => { setMode(null); setScreenshot(null); }}
                    style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer', padding: 0, textAlign: 'left', width: 'fit-content' }}
                  >
                    &larr; Back
                  </button>

                  {/* Title */}
                  <div>
                    <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Title <span style={{ color: '#f87171' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      placeholder={mode === 'bug' ? 'Brief description of the issue' : 'What would you like to see?'}
                      maxLength={200}
                      autoFocus
                      style={{
                        width: '100%', padding: '8px 12px', borderRadius: 6,
                        background: '#060d18', border: '1px solid #1e293b', color: '#e2e8f0',
                        fontSize: 13, outline: 'none', boxSizing: 'border-box',
                      }}
                      onFocus={e => e.target.style.borderColor = mode === 'bug' ? '#f87171' : '#a78bfa'}
                      onBlur={e => e.target.style.borderColor = '#1e293b'}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>Details</label>
                    <textarea
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder={mode === 'bug' ? 'Steps to reproduce, what you expected vs. what happened...' : 'Describe the feature and how it would help your workflow...'}
                      rows={4}
                      style={{
                        width: '100%', padding: '8px 12px', borderRadius: 6,
                        background: '#060d18', border: '1px solid #1e293b', color: '#e2e8f0',
                        fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit',
                        lineHeight: 1.5, boxSizing: 'border-box',
                      }}
                      onFocus={e => e.target.style.borderColor = mode === 'bug' ? '#f87171' : '#a78bfa'}
                      onBlur={e => e.target.style.borderColor = '#1e293b'}
                    />
                  </div>

                  {/* Auto-captured context (bug only) */}
                  {mode === 'bug' && (
                    <div style={{ background: '#060d18', borderRadius: 6, padding: '10px 12px', border: '1px solid #1e293b' }}>
                      <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Auto-captured context
                      </div>
                      {ctx.input && (
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                          <span style={{ color: '#94a3b8' }}>Query:</span>{' '}
                          <span style={{ fontFamily: 'monospace' }}>{ctx.input.length > 100 ? ctx.input.slice(0, 100) + '...' : ctx.input}</span>
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                        <span style={{ color: '#94a3b8' }}>Page:</span> {window.location.pathname}
                      </div>
                      {screenshot ? (
                        <div style={{ marginTop: 6 }}>
                          <div style={{ fontSize: 11, color: '#4ade80', marginBottom: 4 }}>Screenshot captured</div>
                          <img
                            ref={screenshotRef}
                            src={screenshot}
                            alt="Screenshot"
                            style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 4, border: '1px solid #334155', opacity: 0.8 }}
                          />
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Capturing screenshot...</div>
                      )}
                    </div>
                  )}

                  {/* Submit */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                    <button className="btn btn-sm btn-outline-secondary" onClick={handleClose}>Cancel</button>
                    <button
                      className="btn btn-sm"
                      disabled={!title.trim() || submitting}
                      onClick={handleSubmit}
                      style={{
                        background: mode === 'bug' ? '#f87171' : '#a78bfa',
                        color: '#fff', border: 'none', fontWeight: 600, borderRadius: 6, padding: '6px 20px',
                        opacity: !title.trim() || submitting ? 0.5 : 1,
                      }}
                    >
                      {submitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
